import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

export type Lang = 'fr' | 'en' | 'et';
type Vars = Record<string, string | number>;

// Langues disponibles (le français est la langue source : ses clés = les chaînes FR).
// Pour ajouter une langue : créer un dictionnaire (ex: ./es), l'importer et
// l'ajouter ici + dans LOADERS + LOCALES + le type Lang.
export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'et', label: 'Eesti' },
];

// Dictionnaires chargés à la demande : un visiteur francophone ne les télécharge pas.
const LOADERS: Record<Exclude<Lang, 'fr'>, () => Promise<Record<string, string>>> = {
  en: () => import('./en').then((m) => m.en),
  et: () => import('./et').then((m) => m.et),
};
const DICTS: Partial<Record<Lang, Record<string, string>>> = {};

function loadDict(l: Lang): Promise<void> {
  if (l === 'fr' || DICTS[l]) return Promise.resolve();
  return LOADERS[l]().then((d) => {
    DICTS[l] = d;
  });
}

const LOCALES: Record<Lang, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  et: 'et-EE',
};

export function localeOf(lang: Lang): string {
  return LOCALES[lang] || 'fr-FR';
}

interface Ctx {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LangContext = createContext<Ctx>({ lang: 'fr', setLang: () => {} });
const STORAGE_KEY = 'lang';

function isLang(v: any): v is Lang {
  return v === 'fr' || v === 'en' || v === 'et';
}

function initialLang(): Lang {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (isLang(s)) return s;
  } catch {
    /* ignore */
  }
  return 'fr';
}

export function LangProvider({ children }: { children: ReactNode }) {
  // Langue demandée ; la langue active ne bascule qu'une fois son dictionnaire chargé
  const [requested, setRequested] = useState<Lang>(initialLang);
  const [lang, setActive] = useState<Lang>('fr');
  useEffect(() => {
    let cancelled = false;
    loadDict(requested)
      .then(() => {
        if (!cancelled) setActive(requested);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [requested]);
  const setLang = useCallback((l: Lang) => {
    setRequested(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

// Traduction : la chaîne FR est la clé. Dans une autre langue, on cherche dans
// le dictionnaire, sinon on retombe sur le français. Variables : placeholders {nom}.
export function useT() {
  const { lang } = useContext(LangContext);
  return useCallback(
    (key: string, vars?: Vars): string => {
      const dict = DICTS[lang];
      let str = dict ? (dict[key] ?? key) : key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          str = str.split('{' + k + '}').join(String(vars[k]));
        }
      }
      return str;
    },
    [lang],
  );
}
